import { useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { Check, LogOut, MailWarning } from 'lucide-react';
import { AppTopbar } from './AppTopbar';
import { AssistantWidget } from './assistant/AssistantWidget';
import { QuickCreateTask } from './QuickCreateTask';
import { VerticalOnboarding } from './onboarding/VerticalOnboarding';
import { EmailVerificationBanner } from './EmailVerificationBanner';
import { SubscriptionBanner } from './SubscriptionBanner';
import { FeedbackLaunch } from './FeedbackLaunch';
import { TourProvider } from './tour/ProductTour';
import { Button } from './ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth/AuthProvider';
import { IS_EMBEDDED } from '../lib/embed';
import { t } from '../lib/i18n';

/**
 * Cascarón de la app autenticada: topbar + contenido + pie con soporte/crédito.
 *
 * En modo embebido (isla web dentro de la app nativa) se ocultan topbar, pie y el
 * bot flotante: la navegación la da el marco nativo. Se conservan los banners de
 * verificación/suscripción (críticos) y el alta rápida de tarea (?nueva=1).
 *
 * VERIFICACIÓN OBLIGATORIA: si el backend marca `company.email_verification_pending`
 * (empresa que exige verificar + usuario sin verificar), el contenido de la app se
 * REEMPLAZA por un muro bloqueante (`VerifyGateWall`) — la cuenta no puede entrar
 * hasta verificar el email. Las empresas grandfathered (verification_required=0)
 * nunca reciben `email_verification_pending` → no ven el muro (a lo sumo el banner
 * suave). En embebido el flujo lo maneja el marco nativo (sin muro web).
 */
export function ProtectedLayout() {
  const { company } = useAuth();
  const blocked = !IS_EMBEDDED && company?.email_verification_pending === true;

  return (
    <TourProvider>
      <div className="flex min-h-full flex-col bg-bg text-ink">
        {!IS_EMBEDDED && <AppTopbar />}
        <main className="flex-1">
          <div className={IS_EMBEDDED ? 'mx-auto max-w-6xl px-4 py-4' : 'mx-auto max-w-6xl px-4 py-8'}>
            {blocked ? (
              /* Muro de verificación obligatoria: reemplaza toda la app. */
              <VerifyGateWall />
            ) : (
              <>
                {/* Aviso de "verificá tu email" — se autooculta si ya está verificado.
                    Sólo aplica a empresas grandfathered (no bloqueadas). */}
                <EmailVerificationBanner />
                {/* Banner de suscripción bloqueada (pagar para desbloquear) — se
                    autooculta si la empresa está operativa. */}
                <SubscriptionBanner />
                {/* Banner + modal de feedback de lanzamiento (temporal). Se autooculta
                    si el backend no lo habilitó. No aplica en embebido (canal nativo). */}
                {!IS_EMBEDDED && <FeedbackLaunch />}
                <Outlet />
              </>
            )}
          </div>
        </main>
        {/* Pie discreto: Soporte + crédito Evoluado (presentes en toda superficie web). */}
        {!IS_EMBEDDED && (
          <footer className="border-t border-line">
            <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-4 text-xs text-muted">
              <Link to="/soporte" className="transition-colors hover:text-brand-2">
                {t('common.support')}
              </Link>
              <span aria-hidden className="text-line">·</span>
              <a
                href="https://evoluado.com"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-brand-2"
              >
                {t('common.poweredBy')}
              </a>
            </div>
          </footer>
        )}
        {/* Alta rápida de tarea (FAB móvil). Se oculta bajo el muro / solo-lectura / sin proyectos. */}
        {!blocked && <QuickCreateTask />}
        {/* Onboarding P2: elegir rubro en el primer ingreso (registro ya no lo pide).
            No aplica bajo el muro (primero verificar). */}
        {!IS_EMBEDDED && !blocked && <VerticalOnboarding />}
        {/* Bot de ayuda IA (flotante). Se autooculta si el admin no lo habilitó o en embebido. */}
        {!IS_EMBEDDED && !blocked && <AssistantWidget />}
      </div>
    </TourProvider>
  );
}

type ResendState = 'idle' | 'sending' | 'sent' | 'error';

/**
 * Muro de verificación de email OBLIGATORIA — pantalla bloqueante que reemplaza el
 * contenido de la app. Se muestra sólo cuando el backend marca
 * `company.email_verification_pending === true` (el usuario no verificó su email y
 * su empresa lo exige). No hay forma de saltearlo salvo verificar el email; ofrece
 * reenviar el enlace, "ya verifiqué" (revalida contra /auth/me) y cerrar sesión.
 * Tono de aviso --sun, theme-aware (100% tokens).
 */
function VerifyGateWall() {
  const { user, logout, reloadMe } = useAuth();
  const [state, setState] = useState<ResendState>('idle');
  const [rechecking, setRechecking] = useState(false);
  const [stillPending, setStillPending] = useState(false);

  const handleResend = async () => {
    if (state === 'sending' || state === 'sent') return;
    setState('sending');
    try {
      await api.post('/auth/resend-verification');
      setState('sent');
    } catch {
      setState('error');
    }
  };

  const handleRecheck = async () => {
    if (rechecking) return;
    setRechecking(true);
    setStillPending(false);
    try {
      await reloadMe();
      // Si sigue pendiente, /auth/me no cambió `email_verification_pending` y el
      // muro se mantiene: avisamos que todavía no confirmamos el email.
      setStillPending(true);
    } catch {
      setStillPending(true);
    } finally {
      setRechecking(false);
    }
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center py-8">
      <div className="w-full max-w-md rounded-token border border-line bg-surface p-6 text-center shadow-sm sm:p-8">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-sun/15">
          <MailWarning size={28} aria-hidden className="text-sun" />
        </div>
        <h1 className="text-lg font-semibold text-ink">{t('verifyGate.title')}</h1>
        <p className="mt-2 text-sm text-muted">{t('verifyGate.wallBody')}</p>
        {user?.email && (
          <p className="mt-2 break-all text-sm font-medium text-ink">{user.email}</p>
        )}
        <p className="mt-3 text-xs text-muted">{t('verifyGate.wallHint')}</p>

        {stillPending && (
          <p className="mt-4 rounded-token border border-sun/40 bg-sun/10 px-3 py-2 text-xs text-ink">
            {t('verifyGate.stillPending')}
          </p>
        )}
        {state === 'error' && (
          <p className="mt-4 text-xs text-danger">{t('verifyGate.error')}</p>
        )}

        <div className="mt-6 flex flex-col gap-2">
          <Button onClick={handleRecheck} loading={rechecking} disabled={rechecking} className="w-full">
            {t('verifyGate.checked')}
          </Button>
          {state === 'sent' ? (
            <Button variant="secondary" disabled className="w-full gap-1.5">
              <Check size={16} aria-hidden />
              {t('verifyGate.sent')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={handleResend}
              loading={state === 'sending'}
              disabled={state === 'sending'}
              className="w-full"
            >
              {t('verifyGate.resend')}
            </Button>
          )}
          <button
            type="button"
            onClick={() => void logout()}
            className="mt-1 inline-flex items-center justify-center gap-1.5 text-xs text-muted transition-colors hover:text-ink"
          >
            <LogOut size={14} aria-hidden />
            {t('verifyGate.logout')}
          </button>
        </div>
      </div>
    </div>
  );
}
